import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaArrowRight, FaHome, FaBuilding, FaTree, FaPhone, FaWhatsapp, FaEnvelope, FaTrophy, FaHandshake, FaTh, FaComments } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import { propertyApi } from '../services/api';
import './Home.css';

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [featuredRes, allRes] = await Promise.all([
          propertyApi.getFeatured(),
          propertyApi.getAll(),
        ]);
        setFeatured(featuredRes.data?.data || []);
        const all = allRes.data?.data || [];
        setAllProperties(all);
        setRecent(all.slice(0, 6));
      } catch (e) {
        console.error('Error cargando propiedades:', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/propiedades?q=${encodeURIComponent(searchQuery.trim())}`;
    } else {
      window.location.href = '/propiedades';
    }
  };

  return (
    <div className="home">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero__overlay" />
        <div className="hero__content">
          <div className="hero__badge">Agente de Bienes Raíces</div>
          <h1 className="hero__title">
            Tu Hogar Ideal<br />
            <span>Comienza Aquí</span>
          </h1>
          <p className="hero__subtitle">
            Encuentra la propiedad perfecta en El Salvador.<br />
            Casas, apartamentos, terrenos y locales comerciales.
          </p>

          {/* Buscador */}
          <form className="hero__search" onSubmit={handleSearch}>
            <div className="hero__search-input">
              <FaSearch className="hero__search-icon" />
              <input
                type="text"
                placeholder="Busca por ciudad, zona o tipo de propiedad..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn--gold">
              Buscar
            </button>
          </form>

          {/* Quick filters */}
          <div className="hero__filters">
            <a href="/propiedades?type=CASA" className="hero__filter">
              <FaHome /> Casas
            </a>
            <a href="/propiedades?type=APARTAMENTO" className="hero__filter">
              <FaBuilding /> Apartamentos
            </a>
            <a href="/propiedades?type=TERRENO" className="hero__filter">
              <FaTree /> Terrenos
            </a>
            <a href="/propiedades" className="hero__filter">
              Ver todo
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hero__scroll">
          <div className="hero__scroll-dot" />
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="stats">
        <div className="stats__container">
          <div className="stats__item">
            <div className="stats__number">{allProperties.length}+</div>
            <div className="stats__label">Propiedades</div>
          </div>
          <div className="stats__divider" />
          <div className="stats__item">
            <div className="stats__number">10+</div>
            <div className="stats__label">Años de experiencia</div>
          </div>
          <div className="stats__divider" />
          <div className="stats__item">
            <div className="stats__number">500+</div>
            <div className="stats__label">Clientes satisfechos</div>
          </div>
          <div className="stats__divider" />
          <div className="stats__item">
            <div className="stats__number">2</div>
            <div className="stats__label">Países atendidos</div>
          </div>
        </div>
      </section>

      {/* ===== PROPIEDADES DESTACADAS ===== */}
      {featured.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section__header">
              <div className="section__tag">Lo mejor para ti</div>
              <h2 className="section__title">Propiedades Destacadas</h2>
              <p className="section__subtitle">
                Selección exclusiva de nuestras mejores propiedades disponibles
              </p>
            </div>

            {loading ? (
              <div className="grid-skeleton">
                {[1, 2, 3].map(i => <div key={i} className="skeleton-card" />)}
              </div>
            ) : (
              <div className="properties-grid">
                {featured.slice(0, 3).map(property => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            )}

            <div className="section__cta">
              <Link to="/propiedades" className="btn btn--outline-navy">
                Ver todas las propiedades <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ===== PROPIEDADES RECIENTES ===== */}
      <section className="section section--gray">
        <div className="container">
          <div className="section__header">
            <div className="section__tag">Nuevos ingresos</div>
            <h2 className="section__title">Propiedades Disponibles</h2>
          </div>

          {loading ? (
            <div className="grid-skeleton">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="skeleton-card" />)}
            </div>
          ) : recent.length > 0 ? (
            <div className="properties-grid properties-grid--4col">
              {recent.map(property => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <FaHome />
              <p>Próximamente nuevas propiedades</p>
            </div>
          )}

          {allProperties.length > 6 && (
            <div className="section__cta">
              <Link to="/propiedades" className="btn btn--navy">
                Ver todas ({allProperties.length}) <FaArrowRight />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ===== POR QUÉ ELEGIRNOS ===== */}
      <section className="section section--navy">
        <div className="container">
          <div className="section__header section__header--light">
            <div className="section__tag section__tag--gold">¿Por qué nosotros?</div>
            <h2 className="section__title section__title--light">La Diferencia Casa Bonita</h2>
          </div>

          <div className="benefits-grid">
            {[
              { icon: <FaTrophy />, title: 'Experiencia', desc: 'Más de 10 años en el mercado inmobiliario de El Salvador' },
              { icon: <FaHandshake />, title: 'Confianza', desc: 'Más de 500 familias que encontraron su hogar ideal con nosotros' },
              { icon: <FaTh />, title: 'Variedad', desc: 'Amplio portafolio de casas, aptos, terrenos y locales comerciales' },
              { icon: <FaComments />, title: 'Atención Personalizada', desc: 'Te acompañamos en cada paso del proceso de compra o alquiler' },
            ].map((b, i) => (
              <div key={i} className="benefit-card">
                <div className="benefit-card__icon">{b.icon}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== AGENTES ===== */}
      <section className="section">
        <div className="container">
          <div className="section__header">
            <div className="section__tag">Nuestro equipo</div>
            <h2 className="section__title">Nuestras Agentes</h2>
          </div>

          <div className="agents-grid">
            {[
              {
                name: 'Lilian',
                role: 'Agente de Bienes Raíces',
                email: 'linareslilian27@gmail.com',
                phone: '+503 7796-0598',
                initial: 'L',
              },
              {
                name: 'Carol',
                role: 'Agente de Bienes Raíces',
                email: 'clinares79@gmail.com',
                phone: '+503 7941-6355',
                initial: 'C',
              },
            ].map((agent, i) => (
              <div key={i} className="agent-profile">
                <div className="agent-profile__avatar">{agent.initial}</div>
                <h3 className="agent-profile__name">{agent.name}</h3>
                <p className="agent-profile__role">{agent.role}</p>
                <div className="agent-profile__contacts">
                  <a href={`mailto:${agent.email}`} className="agent-contact">
                    <FaEnvelope /> {agent.email}
                  </a>
                  <a href={`tel:${agent.phone.replace(/\s|-/g, '')}`} className="agent-contact">
                    <FaPhone /> {agent.phone}
                  </a>
                </div>
                <a
                  href={`https://wa.me/${agent.phone.replace(/\D/g, '')}?text=${encodeURIComponent('Hola! Me interesa saber más sobre sus propiedades. ¿Me pueden ayudar?')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--whatsapp"
                >
                  <FaWhatsapp /> WhatsApp
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA CONTACTO ===== */}
      <section className="cta-section">
        <div className="cta-section__content">
          <h2>¿Listo para encontrar tu propiedad ideal?</h2>
          <p>Contáctanos hoy y con gusto te asesoramos sin compromiso</p>
          <div className="cta-section__actions">
            <Link to="/contacto" className="btn btn--gold btn--lg">
              Contáctanos ahora
            </Link>
            <a href="tel:+16474700762" className="btn btn--outline-white btn--lg">
              <FaPhone /> +1 (647) 4700762
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
